import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { generateAuditedClient } from "../audit/auditedClient";
import { isAuthorizationError } from "../audit/graphqlErrors";
import { getAuditLogs } from "../graphql/queries";
import { AuditLogRecord, AuditLogPage, GetAuditLogsInput } from "../models/API";
import UsageReportViewer from "./UsageReportViewer";

/**
 * Audit Log Viewer (user-activity-audit-log spec, Requirements 9.3–9.9).
 *
 * A superuser-only surface that lists Audit_Log_Records via the `getAuditLogs`
 * query, mirroring the structure/conventions of `AdminConsoleComponent`
 * (Amplify `generateClient`, MUI Table/Dialog/TextField). Navigation gating is
 * handled separately — the surface lives in the Dashboard section as the
 * `audit-console` plugin, whose entry `visibleDashboardsFor` (dashboardRegistry)
 * shows only to superusers — so this component assumes it is only rendered for
 * a superuser and additionally relies on the server-authoritative pipeline
 * resolver for access control (Req 9.8).
 *
 * Behavior:
 * - On open, fetch the first page and render a row per record with columns:
 *   server timestamp, user alias, team name, operation name, operation type
 *   (Req 9.3).
 * - Filter controls (user alias or numeric user_id, team id, operation name,
 *   start/end datetime) plus a next_token-driven paging control (Req 9.4).
 * - Applying/changing a filter refetches the first page with the combined
 *   active filters, replacing the current list (Req 9.5).
 * - Empty result -> empty-state message, filters kept available (Req 9.6).
 * - Row selection -> detail dialog with the stored (already redacted/truncated)
 *   request_args and response_payload (Req 9.7).
 * - Authorization error -> render only an access-denied message; hide the list,
 *   filters, and paging (Req 9.8).
 * - Any other error -> retrieval-failed message; keep filters for retry
 *   (Req 9.9).
 *
 * The audit-usage-report spec (Req 1.1, 1.2) adds a tab bar to this surface:
 * "Audit log" (the original record list above, unchanged, and the default tab;
 * internal tab value "records") and "Usage Report" (`UsageReportViewer`, which
 * fetches only while its tab is active). Because the whole section is
 * superuser-gated in `MainPage.tsx`, non-superusers see neither the section
 * nor the tabs; the server-side pipeline resolvers remain the authoritative
 * access gate for both views.
 */

interface AuditLogViewerProps {
  isOpen: boolean;
  menuWidth: number;
  /**
   * Render for embedding inside the Dashboard section (audit-console plugin)
   * instead of as a standalone full page. Embedding drops only the page
   * chrome — the `main` landmark, the nav-rail margin, the fixed 100vh
   * height, and the own title (DashboardHeader supplies it) — while the
   * tabs, filters, dialogs, and error branches behave identically.
   */
  embedded?: boolean;
}

/** Active (applied) filter set sent to `getAuditLogs`, sans pagination fields. */
type AuditFilters = Omit<GetAuditLogsInput, "next_token" | "page_size">;

/** Retrieval outcome used to switch between the normal, denied, and error UIs. */
type ViewerStatus = "ok" | "auth-error" | "error";

/** Views selectable from the tab bar (audit-usage-report spec, Req 1.1). */
type AuditTab = "records" | "usage";

/** Format an ISO-8601 server timestamp for display, falling back to the raw value. */
const formatTimestamp = (ts: string): string => {
  if (!ts) return "";
  const d = new Date(ts);
  return isNaN(d.getTime()) ? ts : d.toLocaleString();
};

/**
 * Convert a `<input type="datetime-local">` value (local wall-clock, no zone)
 * into an ISO-8601 UTC string, or null when empty/unparseable.
 */
const toIso = (local: string): string | null => {
  if (!local) return null;
  const d = new Date(local);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * Pretty-print a stored AWSJSON string for the detail dialog. The value is
 * displayed exactly as returned (already redacted/truncated at write time,
 * Req 9.7); JSON is indented when parseable, otherwise shown verbatim.
 */
const prettyJson = (value?: string | null): string => {
  if (value === null || value === undefined || value === "") return "";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

export const AuditLogViewer: React.FC<AuditLogViewerProps> = ({
  isOpen,
  menuWidth,
  embedded = false,
}) => {
  const theme = useTheme();
  const client = useMemo(() => generateAuditedClient(), []);

  // Draft filter inputs (edited freely; only committed on Apply/Clear).
  const [userInput, setUserInput] = useState("");
  const [teamInput, setTeamInput] = useState("");
  const [opNameInput, setOpNameInput] = useState("");
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");

  // The filters actually driving the current result set. Kept separate from the
  // draft inputs so pagination reuses the same filter set the page was built
  // with (Req 8.5 / 9.5).
  const [appliedFilters, setAppliedFilters] = useState<AuditFilters>({});

  // Result + status.
  const [records, setRecords] = useState<AuditLogRecord[]>([]);
  const [status, setStatus] = useState<ViewerStatus>("ok");
  const [loading, setLoading] = useState(false);

  // Pagination: `nextToken` fetches the following page; `prevTokens` is the
  // stack of tokens used for already-visited pages so the user can page back.
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [prevTokens, setPrevTokens] = useState<(string | null)[]>([]);
  const [currentToken, setCurrentToken] = useState<string | null>(null);

  // Selected record for the detail dialog (Req 9.7).
  const [selected, setSelected] = useState<AuditLogRecord | null>(null);

  // Active tab: the pre-existing record list ("records", default) or the
  // usage report ("usage") (audit-usage-report spec, Req 1.1).
  const [activeTab, setActiveTab] = useState<AuditTab>("records");

  /** Parse the draft inputs into an applied filter set for `getAuditLogs`. */
  const buildFiltersFromInputs = (): AuditFilters => {
    const filters: AuditFilters = {};
    const user = userInput.trim();
    if (user) {
      // A purely numeric value is treated as a user_id; anything else is an
      // alias (Req 9.4 "user (alias or user_id)").
      if (/^\d+$/.test(user)) filters.user_id = Number(user);
      else filters.user_alias = user;
    }
    const team = teamInput.trim();
    if (team && /^\d+$/.test(team)) filters.team_id = Number(team);
    const op = opNameInput.trim();
    if (op) filters.op_name = op;
    const start = toIso(startInput);
    if (start) filters.start_time = start;
    const end = toIso(endInput);
    if (end) filters.end_time = end;
    return filters;
  };

  /**
   * Run a single `getAuditLogs` request for the given filters/token and reflect
   * the outcome in state. Never throws; classifies failures for Req 9.8 / 9.9.
   */
  const runQuery = async (filters: AuditFilters, token: string | null) => {
    setLoading(true);
    try {
      const input: GetAuditLogsInput = { ...filters };
      if (token) input.next_token = token;
      const result = await client.graphql({
        query: getAuditLogs,
        variables: { input },
      });
      const page: AuditLogPage | null =
        (result as any)?.data?.getAuditLogs ?? null;
      setRecords(page?.records ?? []);
      setNextToken(page?.next_token ?? null);
      setStatus("ok");
    } catch (err) {
      console.error("Failed to retrieve audit logs:", err);
      // On failure clear the list/paging; the branch-specific message is driven
      // by `status`. Filters stay intact for retry on the generic-error branch.
      setRecords([]);
      setNextToken(null);
      setStatus(isAuthorizationError(err) ? "auth-error" : "error");
    } finally {
      setLoading(false);
    }
  };

  /** Commit the draft inputs and (re)load the first page (Req 9.5). */
  const applyFilters = () => {
    const filters = buildFiltersFromInputs();
    setAppliedFilters(filters);
    setPrevTokens([]);
    setCurrentToken(null);
    runQuery(filters, null);
  };

  /** Reset all filters and reload the first, unfiltered page (Req 9.5, 9.6). */
  const clearFilters = () => {
    setUserInput("");
    setTeamInput("");
    setOpNameInput("");
    setStartInput("");
    setEndInput("");
    const filters: AuditFilters = {};
    setAppliedFilters(filters);
    setPrevTokens([]);
    setCurrentToken(null);
    runQuery(filters, null);
  };

  /** Advance to the next page using the returned pagination token (Req 9.4). */
  const goNext = () => {
    if (!nextToken) return;
    setPrevTokens((stack) => [...stack, currentToken]);
    setCurrentToken(nextToken);
    runQuery(appliedFilters, nextToken);
  };

  /** Return to the previous page using the retained token stack (Req 9.4). */
  const goPrev = () => {
    if (prevTokens.length === 0) return;
    const stack = [...prevTokens];
    const prev = stack.pop() ?? null;
    setPrevTokens(stack);
    setCurrentToken(prev);
    runQuery(appliedFilters, prev);
  };

  // Load the first page each time the viewer opens, reusing the last applied
  // filter set (empty on first open) (Req 9.3).
  useEffect(() => {
    if (isOpen) {
      setPrevTokens([]);
      setCurrentToken(null);
      runQuery(appliedFilters, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const detailRows: [string, string][] = selected
    ? [
        ["Record ID", selected.record_id],
        ["Timestamp", formatTimestamp(selected.server_ts)],
        ["Operation", selected.op_name],
        ["Type", selected.op_type],
        ["User", `${selected.user_alias} (${selected.user_id})`],
        ["Team", `${selected.team_name} (${selected.team_id})`],
        ["Actor source", selected.actor_source],
        ["Truncated", selected.truncated ? "Yes" : "No"],
      ]
    : [];

  const preStyle = {
    m: 0,
    p: 1.5,
    bgcolor: "grey.100",
    borderRadius: 1,
    fontFamily: "monospace",
    fontSize: "0.8rem",
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    maxHeight: 300,
    overflow: "auto",
  };

  return (
    <Box
      component={embedded ? "div" : "main"}
      sx={
        embedded
          ? {
              // Embedded in the Dashboard section: DashboardComponent owns the
              // main landmark, margins, page height, and scrolling, so content
              // flows naturally instead of filling a fixed viewport.
              display: "flex",
              flexDirection: "column",
            }
          : {
              flexGrow: 1,
              ml: `${menuWidth}px`,
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              transition: theme.transitions.create("margin-left", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
            }
      }
    >
      {/* Fixed Header */}
      <Box sx={{ flexShrink: 0, p: 2, pb: 0 }}>
        {/* When embedded, DashboardHeader already shows the "Audit Console"
            title, so the own title is dropped to avoid duplication. */}
        {!embedded && (
          <Typography variant="h5" fontWeight={700}>
            Audit Console
          </Typography>
        )}
        {/* Tab bar (audit-usage-report spec, Req 1.1, 1.2). The Audit Console
            surface is superuser-gated where it is mounted (the audit-console
            dashboard entry via visibleDashboardsFor), so non-superusers see
            neither the surface nor these tabs; the server-side pipeline
            resolvers remain the authoritative access gate for both views. */}
        <Tabs
          value={activeTab}
          onChange={(_event, value: AuditTab) => setActiveTab(value)}
          aria-label="Audit console views"
          sx={{ mt: 1 }}
        >
          <Tab
            label="Audit log"
            value="records"
            id="audit-log-tab-records"
            aria-controls="audit-log-tabpanel-records"
          />
          <Tab
            label="Usage Report"
            value="usage"
            id="audit-log-tab-usage"
            aria-controls="audit-log-tabpanel-usage"
          />
        </Tabs>
      </Box>

      {/* "Audit log" tab (internal value "records"): the pre-existing audit
          record list UI, unchanged. Kept mounted while hidden so filters,
          results, and paging state survive tab switches. */}
      <Box
        role="tabpanel"
        hidden={activeTab !== "records"}
        id="audit-log-tabpanel-records"
        aria-labelledby="audit-log-tab-records"
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: activeTab === "records" ? "flex" : "none",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {status === "auth-error" ? (
          // Req 9.8: render ONLY the access-denied message; hide list/filters/paging.
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              You do not have access to the audit log.
            </Alert>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2, pb: 2 }}>
            {/* Filter controls (Req 9.4) */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <TextField
                  label="User (alias or ID)"
                  size="small"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  sx={{ width: 200 }}
                />
                <TextField
                  label="Team ID"
                  size="small"
                  value={teamInput}
                  onChange={(e) => setTeamInput(e.target.value)}
                  sx={{ width: 140 }}
                />
                <TextField
                  label="Operation name"
                  size="small"
                  value={opNameInput}
                  onChange={(e) => setOpNameInput(e.target.value)}
                  sx={{ width: 200 }}
                />
                <TextField
                  label="Start"
                  type="datetime-local"
                  size="small"
                  value={startInput}
                  onChange={(e) => setStartInput(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 220 }}
                />
                <TextField
                  label="End"
                  type="datetime-local"
                  size="small"
                  value={endInput}
                  onChange={(e) => setEndInput(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 220 }}
                />
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  disabled={loading}
                >
                  Apply Filters
                </Button>
                <Button onClick={clearFilters} disabled={loading}>
                  Clear
                </Button>
              </Box>
            </Paper>

            {status === "error" && (
              // Req 9.9: retrieval-failed message; filters remain available for retry.
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to retrieve audit records. Adjust the filters and apply
                again to retry.
              </Alert>
            )}

            {status === "ok" && (
              <>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Timestamp</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Team</TableCell>
                        <TableCell>Operation</TableCell>
                        <TableCell>Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <CircularProgress size={24} sx={{ my: 2 }} />
                          </TableCell>
                        </TableRow>
                      ) : records.length === 0 ? (
                        // Req 9.6: empty-state message; filters stay available above.
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              py={2}
                            >
                              No audit records match the current filters.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        records.map((record) => (
                          <TableRow
                            hover
                            key={record.record_id}
                            onClick={() => setSelected(record)}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell>
                              {formatTimestamp(record.server_ts)}
                            </TableCell>
                            <TableCell>{record.user_alias}</TableCell>
                            <TableCell>{record.team_name}</TableCell>
                            <TableCell>{record.op_name}</TableCell>
                            <TableCell>{record.op_type}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Paging controls (Req 9.4) */}
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="flex-end"
                  alignItems="center"
                  sx={{ mt: 2 }}
                >
                  <Button
                    onClick={goPrev}
                    disabled={loading || prevTokens.length === 0}
                  >
                    Previous
                  </Button>
                  <Button onClick={goNext} disabled={loading || !nextToken}>
                    Next page
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Usage Report tab (audit-usage-report spec, Req 1.1). The viewer stays
          mounted so its selections survive tab switches; isActive gates its
          fetching to while this tab is active. */}
      <Box
        role="tabpanel"
        hidden={activeTab !== "usage"}
        id="audit-log-tabpanel-usage"
        aria-labelledby="audit-log-tab-usage"
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: activeTab === "usage" ? "block" : "none",
          overflowY: "auto",
        }}
      >
        <UsageReportViewer isActive={activeTab === "usage"} />
      </Box>

      {/* Record detail dialog (Req 9.7) */}
      <Dialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Audit Record Detail</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {detailRows.map(([label, value]) => (
                  <Stack key={label} direction="row" spacing={1}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ minWidth: 120 }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Request Arguments
              </Typography>
              <Box component="pre" sx={preStyle}>
                {prettyJson(selected.request_args) || "(empty)"}
              </Box>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Response Payload
              </Typography>
              <Box component="pre" sx={preStyle}>
                {prettyJson(selected.response_payload) || "(empty)"}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogViewer;
